// The editor subsystem: a flat Zed-style tab strip, a hairline breadcrumb, a
// showcase welcome state, and the Monaco editor wired to the store. Diagnostics
// flow store → Monaco model markers so the gutter, squiggles, and the Problems
// panel all read the same source.
//
// Monaco is served from the locally bundled `monaco-editor` (loader.config with
// `monaco`), never a CDN. Editing and compiling need no network at all; the one
// outbound call in the app is the optional mempool.space price lookup.
//
// Icons are lucide-react throughout; nothing here is hand-drawn.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Editor, { loader, useMonaco, type OnMount } from "@monaco-editor/react";
import type * as monacoEditor from "monaco-editor";
import {
  ArrowRight,
  Box,
  Braces,
  ChevronRight,
  Command,
  CornerDownLeft,
  FileCode2,
  FilePlus2,
  FileText,
  X,
  type LucideProps,
} from "lucide-react";
import monaco from "./monaco";
import { useIde } from "../store";
import { NEW_CONTRACT_TEMPLATE } from "../fs/examples";
import type { Diagnostic, Severity } from "../types";
import { registerBasis } from "./sealLanguage";
import { setActiveEditor } from "./editorHandle";
import { VIM_STATUS_ID } from "../shell/StatusBar";
import { monacoThemeFor } from "../theme";
import "./editor.css";

type MonacoNS = typeof import("monaco-editor");
type IStandaloneCodeEditor = monacoEditor.editor.IStandaloneCodeEditor;
type Language = "seal" | "json" | "plaintext";

// Wire @monaco-editor/react to the bundled monaco instead of fetching it from a
// CDN. Idempotent, calling config more than once is harmless, but we still
// guard so HMR doesn't thrash it.
let loaderConfigured = false;
function configureLoaderOnce(): void {
  if (loaderConfigured) return;
  loaderConfigured = true;
  loader.config({ monaco });
}
configureLoaderOnce();

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

function severityToMarker(monaco: MonacoNS, sev: Severity): monacoEditor.MarkerSeverity {
  switch (sev) {
    case "error":
      return monaco.MarkerSeverity.Error;
    case "warning":
      return monaco.MarkerSeverity.Warning;
    case "help":
      return monaco.MarkerSeverity.Hint;
    case "note":
    default:
      return monaco.MarkerSeverity.Info;
  }
}

function diagnosticsToMarkers(
  monaco: MonacoNS,
  diagnostics: Diagnostic[],
): monacoEditor.editor.IMarkerData[] {
  return diagnostics
    .filter((d) => d.file === "source")
    .map((d) => {
      // Guard against a zero-width / inverted span so the squiggle is visible.
      const startLine = Math.max(1, d.start.line);
      const startCol = Math.max(1, d.start.col);
      let endLine = Math.max(startLine, d.end.line);
      let endCol = d.end.col;
      if (endLine === startLine && endCol <= startCol) endCol = startCol + 1;
      if (endLine < startLine) {
        endLine = startLine;
        endCol = startCol + 1;
      }
      const related =
        d.notes.length > 0
          ? d.notes.map((n) => `\n${n.severity}: ${n.message}`).join("")
          : "";
      return {
        severity: severityToMarker(monaco, d.severity),
        message: `[${d.code}] ${d.message}${related}`,
        startLineNumber: startLine,
        startColumn: startCol,
        endLineNumber: endLine,
        endColumn: Math.max(startCol + 1, endCol),
        source: "seal",
      };
    });
}

const MARKER_OWNER = "seal";

// --------------------------------------------------------------------------
// Small presentational helpers
// --------------------------------------------------------------------------

/** A language-typed file glyph, tinted to match the explorer (accent / gold). */
function FileGlyph({ language, size = 15 }: { language: Language; size?: number }) {
  const Icon = language === "json" ? Braces : FileCode2;
  const tone = language === "json" ? "ed-glyph--json" : "ed-glyph--seal";
  return (
    <span className={`ed-glyph ${tone}`} aria-hidden="true">
      <Icon size={size} strokeWidth={1.75} />
    </span>
  );
}

/** Render a keybinding as discrete key caps, e.g. ⇧ ⌘ P. */
function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="ed-keys" aria-hidden="true">
      {keys.map((k, i) => (
        <kbd className="ed-kbd" key={`${k}-${i}`}>
          {k}
        </kbd>
      ))}
    </span>
  );
}

/** Loosely parse `contract <Name>` out of a .sl source for the breadcrumb. */
function parseContractName(source: string): string | null {
  const m = /(^|\n)\s*contract\s+([A-Za-z_]\w*)/.exec(source);
  return m ? m[2] : null;
}

/** Short human-readable description for the recent-contracts list. */
function describeContract(source: string): string {
  const m = /^\s*\/\/\s?(.+)$/m.exec(source);
  if (!m) return "Seal contract";
  const line = m[1].trim();
  return line.length > 64 ? `${line.slice(0, 63)}…` : line;
}

// --------------------------------------------------------------------------
// Tab strip
// --------------------------------------------------------------------------

interface TabProps {
  path: string;
  active: boolean;
}

function Tab({ path, active }: TabProps) {
  const file = useIde((s) => s.files[path]);
  const setActive = useIde((s) => s.setActive);
  const closeTab = useIde((s) => s.closeTab);

  const onClose = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      closeTab(path);
    },
    [closeTab, path],
  );

  const onMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      // Middle-click closes, mirroring browser/IDE convention.
      if (e.button === 1) {
        e.preventDefault();
        closeTab(path);
      }
    },
    [closeTab, path],
  );

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setActive(path);
      }
    },
    [setActive, path],
  );

  if (!file) return null;

  return (
    <div
      className={`ed-tab${active ? " ed-tab--active" : ""}`}
      role="tab"
      aria-selected={active}
      tabIndex={0}
      title={path}
      onClick={() => setActive(path)}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
    >
      <FileGlyph language={file.language} size={14} />
      <span className="ed-tab-name">{file.path}</span>
      <button
        type="button"
        className="ed-tab-close"
        aria-label={`Close ${path}`}
        title="Close"
        onClick={onClose}
      >
        <X size={13} strokeWidth={2} />
      </button>
    </div>
  );
}

function TabStrip() {
  const openTabs = useIde((s) => s.openTabs);
  const activePath = useIde((s) => s.activePath);

  if (openTabs.length === 0) return null;

  return (
    <div className="ed-tabstrip" role="tablist" aria-label="Open editors">
      {openTabs.map((path) => (
        <Tab key={path} path={path} active={path === activePath} />
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
// Breadcrumb
// --------------------------------------------------------------------------

function Breadcrumb() {
  const activePath = useIde((s) => s.activePath);
  const file = useIde((s) => (activePath ? s.files[activePath] : undefined));

  const contractName = useMemo(
    () => (file && file.language === "seal" ? parseContractName(file.content) : null),
    [file],
  );

  if (!file) return null;

  return (
    <nav className="ed-breadcrumb" aria-label="Breadcrumb">
      <span className="ed-crumb">
        <FileGlyph language={file.language} size={13} />
        <span className="ed-crumb-text">{file.path}</span>
      </span>
      {contractName && (
        <>
          <ChevronRight className="ed-crumb-sep" size={13} strokeWidth={1.75} aria-hidden="true" />
          <span className="ed-crumb ed-crumb--symbol">
            <Box size={13} strokeWidth={1.75} aria-hidden="true" />
            <span className="ed-crumb-text">
              contract <span className="ed-crumb-name">{contractName}</span>
            </span>
          </span>
        </>
      )}
    </nav>
  );
}

// --------------------------------------------------------------------------
// Welcome / empty state
// --------------------------------------------------------------------------

interface WelcomeAction {
  icon: ComponentType<LucideProps>;
  label: string;
  hint: string;
  keys?: string[];
  onClick: () => void;
}

/** The Seal mascot: a cozy cream seal with a soft face, peach cheeks and
 *  whiskers. Fixed friendly colors rather than theme-tracking, so it reads as a
 *  character. */
function WelcomeMark() {
  const ref = useRef<HTMLSpanElement>(null);
  // Boop: replay the squash-and-stretch on every tap by removing the class,
  // forcing a reflow, then re-adding it, so rapid taps always re-trigger.
  const boop = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("is-boop");
    void el.offsetWidth;
    el.classList.add("is-boop");
  }, []);
  return (
    <span
      ref={ref}
      className="ed-welcome-mark"
      role="button"
      tabIndex={0}
      aria-label="Boop Selkie"
      title="Selkie"
      onPointerDown={(e) => {
        if (e.button === 0) boop();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          boop();
        }
      }}
      onAnimationEnd={(e) => {
        if (e.animationName === "seal-boop") ref.current?.classList.remove("is-boop");
      }}
    >
      <svg width="74" height="62" viewBox="0 0 120 100" fill="none">
        <ellipse cx="22" cy="72" rx="7" ry="4.6" fill="#fbf7ef" transform="rotate(-26 22 72)" />
        <path d="M18 58 C18 32 38 18 60 18 C82 18 102 32 102 58 C102 75 89 83 73 83.5 C65 84 55 84 47 83.5 C31 83 18 75 18 58 Z" fill="#fbf7ef" />
        <circle cx="43" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
        <circle cx="77" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
        <g className="seal-eye">
          <ellipse cx="51.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
          <circle cx="52.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
        </g>
        <g className="seal-eye">
          <ellipse cx="68.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
          <circle cx="69.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
        </g>
        {/* happy closed eyes, shown only while smiling */}
        <g className="bm-eyes-happy">
          <path d="M47.5 54 Q51.5 49.5 55.5 54" stroke="#5f4030" strokeWidth="2" strokeLinecap="round" />
          <path d="M64.5 54 Q68.5 49.5 72.5 54" stroke="#5f4030" strokeWidth="2" strokeLinecap="round" />
        </g>
        <ellipse cx="60" cy="60.5" rx="3.3" ry="2.4" fill="#6b4a36" />
        <path d="M60 62.9 L60 66" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        <g className="bm-neutral">
          <path d="M60 66 Q55.6 70.4 51.8 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M60 66 Q64.4 70.4 68.2 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        </g>
        <path className="bm-grin" d="M52 65.8 Q60 71.8 68 65.8" stroke="#6b4a36" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M48 59.5 Q40 58 32.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M48 63.5 Q40.5 65 33.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M72 59.5 Q80 58 87.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M72 63.5 Q79.5 65 86.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/** Selkie, the floating companion: a small seal in the editor's corner. He idles
 *  (bob + blink), hops happy when a compile proves safe to fund, frowns then
 *  holds a skeptical brow when the gate refuses, and grins when tapped. Soft +
 *  low-opacity at rest so he stays out of the way. */
function SealBuddy() {
  const status = useIde((s) => s.status);
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef(status);

  const playReaction = useCallback((type: "happy" | "worried") => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("is-happy", "is-worried");
    void el.offsetWidth; // reflow so the animation replays every time
    el.classList.add(`is-${type}`);
  }, []);

  // React the moment a compile lands. A clean, fundable compile clears any held
  // doubt (the raised eyebrow) and he hops; a refusal/error makes him frown, then
  // -- once the shiver settles (see onAnimationEnd) -- he sets into a raised-
  // eyebrow skepticism he holds until the next clean compile. A bare "needs
  // values" result leaves his mood untouched.
  useEffect(() => {
    const wasCompiling = prev.current === "compiling";
    prev.current = status;
    if (!wasCompiling || status === "compiling") return;
    const el = ref.current;
    if (!el) return;
    const gate = useIde.getState().result?.gate;
    if (status === "ok" && gate?.mayProceed) {
      el.classList.remove("mood-skeptical");
      playReaction("happy");
    } else if (status === "error" || gate?.divergence || (gate && !gate.mayProceed)) {
      playReaction("worried");
    }
  }, [status, playReaction]);

  return (
    <span
      ref={ref}
      className="ed-buddy"
      role="button"
      tabIndex={0}
      aria-label="Boop Selkie"
      title="Selkie"
      onPointerDown={(e) => {
        if (e.button === 0) playReaction("happy");
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          playReaction("happy");
        }
      }}
      onAnimationEnd={(e) => {
        const el = ref.current;
        if (!el) return;
        if (e.animationName === "seal-buddy-happy") {
          el.classList.remove("is-happy");
        } else if (e.animationName === "seal-buddy-worried") {
          el.classList.remove("is-worried");
          el.classList.add("mood-skeptical"); // settle into the held raised eyebrow
        }
      }}
    >
      <svg width="60" height="46" viewBox="14 16 88 68" fill="none" aria-hidden="true">
        <ellipse cx="22" cy="72" rx="7" ry="4.6" fill="#fbf7ef" transform="rotate(-26 22 72)" />
        <path d="M18 58 C18 32 38 18 60 18 C82 18 102 32 102 58 C102 75 89 83 73 83.5 C65 84 55 84 47 83.5 C31 83 18 75 18 58 Z" fill="#fbf7ef" />
        <circle cx="43" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
        <circle cx="77" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
        {/* worried brows, shown only on a failed verdict */}
        <g className="bm-brows">
          <path d="M46.5 48.5 Q51 46.4 55.5 47" stroke="#6b4a36" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M73.5 48.5 Q69 46.4 64.5 47" stroke="#6b4a36" strokeWidth="1.3" strokeLinecap="round" />
        </g>
        {/* held skepticism: one brow raised, the other furrowed (upside down),
            kept after a failure until the next clean compile */}
        <path className="bm-skeptic-brow" d="M64 46.5 Q68.5 43.5 73 46" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        <path className="bm-skeptic-brow" d="M47 45 Q51.5 48 56 45" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        <g className="seal-eye">
          <ellipse cx="51.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
          <circle cx="52.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
        </g>
        <g className="seal-eye">
          <ellipse cx="68.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
          <circle cx="69.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
        </g>
        {/* happy closed eyes, shown only while smiling */}
        <g className="bm-eyes-happy">
          <path d="M47.5 54 Q51.5 49.5 55.5 54" stroke="#5f4030" strokeWidth="2" strokeLinecap="round" />
          <path d="M64.5 54 Q68.5 49.5 72.5 54" stroke="#5f4030" strokeWidth="2" strokeLinecap="round" />
        </g>
        <ellipse cx="60" cy="60.5" rx="3.3" ry="2.4" fill="#6b4a36" />
        <path d="M60 62.9 L60 66" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        {/* mouth: neutral by default, a grin on success, a frown on failure */}
        <g className="bm-neutral">
          <path d="M60 66 Q55.6 70.4 51.8 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M60 66 Q64.4 70.4 68.2 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
        </g>
        <path className="bm-grin" d="M52 65.8 Q60 71.8 68 65.8" stroke="#6b4a36" strokeWidth="1.5" strokeLinecap="round" />
        <path className="bm-frown" d="M51 69.5 Q60 65 69 69.5" stroke="#6b4a36" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M48 59.5 Q40 58 32.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M48 63.5 Q40.5 65 33.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M72 59.5 Q80 58 87.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M72 63.5 Q79.5 65 86.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function Welcome() {
  const files = useIde((s) => s.files);
  const newFile = useIde((s) => s.newFile);
  const openFile = useIde((s) => s.openFile);
  const setCommandPalette = useIde((s) => s.setCommandPalette);
  const runCompile = useIde((s) => s.runCompile);

  const onNew = useCallback(() => {
    // A unique untitled path so we never collide with an existing file.
    let n = 1;
    let path = "untitled.sl";
    while (files[path]) {
      n += 1;
      path = `untitled-${n}.sl`;
    }
    newFile(path, NEW_CONTRACT_TEMPLATE);
  }, [files, newFile]);

  // The seed contracts, surfaced as a "Recent" list. Stable, .sl only.
  const recents = useMemo(
    () =>
      Object.values(files)
        .filter((file) => file.language === "seal")
        .sort((a, b) => a.path.localeCompare(b.path)),
    [files],
  );

  const actions = useMemo<WelcomeAction[]>(
    () => [
      {
        icon: FilePlus2,
        label: "New contract",
        hint: "Start from the template",
        onClick: onNew,
      },
      {
        icon: Command,
        label: "Command palette",
        hint: "Search every command",
        keys: ["⇧", MOD, "P"],
        onClick: () => setCommandPalette(true),
      },
      {
        icon: CornerDownLeft,
        label: "Compile to address",
        hint: "Certify, then fund",
        keys: [MOD, "↵"],
        // Nothing is open on the welcome screen, so compiling straight away
        // would do nothing at all. Open the first contract, then compile it.
        onClick: () => {
          const first = recents[0];
          if (first) openFile(first.path);
          void runCompile("fund");
        },
      },
    ],
    [onNew, setCommandPalette, runCompile, openFile, recents],
  );

  return (
    <div className="ed-welcome">
      <div className="ed-welcome-inner">
        <header className="ed-welcome-head">
          <WelcomeMark />
          <div className="ed-welcome-titles">
            <h1 className="ed-welcome-title">Seal</h1>
            <p className="ed-welcome-tagline">
              A verification language for Bitcoin tapscript.
            </p>
          </div>
        </header>

        <div className="ed-welcome-cols">
          <section className="ed-welcome-section" aria-label="Start">
            <h2 className="ed-welcome-label label-caps">Start</h2>
            <ul className="ed-action-list">
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <li key={a.label}>
                    <button type="button" className="ed-action" onClick={a.onClick}>
                      <span className="ed-action-icon" aria-hidden="true">
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="ed-action-body">
                        <span className="ed-action-label">{a.label}</span>
                        <span className="ed-action-hint">{a.hint}</span>
                      </span>
                      {a.keys ? (
                        <Keys keys={a.keys} />
                      ) : (
                        <ArrowRight
                          className="ed-action-arrow"
                          size={14}
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          {recents.length > 0 && (
            <section className="ed-welcome-section" aria-label="Recent contracts">
              <h2 className="ed-welcome-label label-caps">Recent</h2>
              <ul className="ed-recent-list">
                {recents.map((file) => (
                  <li key={file.path}>
                    <button
                      type="button"
                      className="ed-recent"
                      title={file.path}
                      onClick={() => openFile(file.path)}
                    >
                      <FileText
                        className="ed-recent-icon"
                        size={15}
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                      <span className="ed-recent-body">
                        <span className="ed-recent-name">{file.path}</span>
                        <span className="ed-recent-desc">
                          {describeContract(file.content)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Editor root
// --------------------------------------------------------------------------

export function EditorArea() {
  const activePath = useIde((s) => s.activePath);
  const file = useIde((s) => (activePath ? s.files[activePath] : undefined));
  const setContent = useIde((s) => s.setContent);
  const diagnostics = useIde((s) => s.diagnostics);
  const theme = useIde((s) => s.prefs.theme);
  const fontSize = useIde((s) => s.prefs.fontSize);
  const minimap = useIde((s) => s.prefs.minimap);
  const wordWrap = useIde((s) => s.prefs.wordWrap);

  const monaco = useMonaco();
  const editorRef = useRef<IStandaloneCodeEditor | null>(null);

  const language: Language = file?.language ?? "plaintext";
  // Every language uses the active theme's editor palette, including the
  // `.args.json` companions, so they sit on the same background as everything
  // else rather than Monaco's stark built-in vs / vs-dark. The theme rules key
  // off generic token classes (string/number/keyword/...) that Monaco emits for
  // JSON too, so the colouring carries over.
  const editorTheme = monacoThemeFor(theme);

  // Register Seal as soon as monaco is available (covers the case where the
  // editor for a non-seal file mounts first, then a .sl tab is opened).
  useEffect(() => {
    if (monaco) registerBasis(monaco);
  }, [monaco]);

  // Keep the editor theme in sync with prefs + active language.
  useEffect(() => {
    if (monaco) monaco.editor.setTheme(editorTheme);
  }, [monaco, editorTheme]);

  // Diagnostics → model markers. Only "source"-file diagnostics target the .sl
  // model. Markers are scoped to the active model and cleared when it changes.
  useEffect(() => {
    if (!monaco) return;
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!model) return;

    if (language === "seal") {
      monaco.editor.setModelMarkers(model, MARKER_OWNER, diagnosticsToMarkers(monaco, diagnostics));
    } else {
      monaco.editor.setModelMarkers(model, MARKER_OWNER, []);
    }
  }, [monaco, diagnostics, language, activePath]);

  // The editor is keyed by path, so switching files disposes it and builds a
  // new one. Vim has to follow that, and a ref would not re-run the effect.
  const [liveEditor, setLiveEditor] = useState<IStandaloneCodeEditor | null>(null);
  const editorMode = useIde((s) => s.prefs.editorMode);

  // Vim mode, loaded only when it is switched on: the keymap is megabytes, and
  // someone who never enables it should not pay for it on first paint.
  useEffect(() => {
    if (!liveEditor || editorMode !== "vim") return;
    let vim: { dispose(): void } | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const { initVimMode } = await import("monaco-vim");
        if (cancelled) return;
        vim = initVimMode(liveEditor, document.getElementById(VIM_STATUS_ID));
      } catch (err) {
        console.error("vim mode failed to load:", err);
      }
    })();
    return () => {
      cancelled = true;
      vim?.dispose();
      // The status node belongs to the status bar, so leave it as we found it.
      const node = document.getElementById(VIM_STATUS_ID);
      if (node) node.textContent = "";
    };
  }, [liveEditor, editorMode]);

  // Reveal a (line, col) when a panel row is clicked. `gotoEditor` in
  // panels/shared.tsx dispatches this on window; this is the other half.
  useEffect(() => {
    const onGoto = (e: Event) => {
      const { line, col } = (e as CustomEvent<{ line: number; col: number }>).detail ?? {};
      const editor = editorRef.current;
      if (!editor || typeof line !== "number") return;
      const position = { lineNumber: line, column: typeof col === "number" ? col : 1 };
      editor.revealPositionInCenter(position);
      editor.setPosition(position);
      editor.focus();
    };
    window.addEventListener("seal:goto", onGoto);
    return () => window.removeEventListener("seal:goto", onGoto);
  }, []);

  // What `onMount` needs to read at mount time. It goes through a ref because the
  // editor is keyed by path below, so it remounts on every file switch and
  // `onMount` runs again each time. Binding these in the dependency list would
  // either capture first-render values (which is what made every newly opened
  // file snap back to the default cream palette, whatever theme was selected) or
  // rebuild the callback on every keystroke, since `file` changes as you type.
  const mountRef = useRef({ editorTheme, isBasis: language === "seal" });
  mountRef.current = { editorTheme, isBasis: language === "seal" };

  const onMount = useCallback<OnMount>(
    (editor, monacoInstance) => {
      editorRef.current = editor;
      setActiveEditor(editor);
      setLiveEditor(editor);
      editor.onDidDispose(() => {
        setActiveEditor(null);
        setLiveEditor((cur) => (cur === editor ? null : cur));
      });
      registerBasis(monacoInstance);
      const { editorTheme: mountTheme, isBasis } = mountRef.current;
      monacoInstance.editor.setTheme(mountTheme);

      // Apply any diagnostics already in the store at mount time.
      const model = editor.getModel();
      if (model && isBasis) {
        monacoInstance.editor.setModelMarkers(
          model,
          MARKER_OWNER,
          diagnosticsToMarkers(monacoInstance, useIde.getState().diagnostics),
        );
      }
    },
    [],
  );

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (activePath && value !== undefined) setContent(activePath, value);
    },
    [activePath, setContent],
  );

  const options = useMemo<monacoEditor.editor.IStandaloneEditorConstructionOptions>(
    () => ({
      fontSize,
      fontFamily: "var(--font-mono)",
      fontLigatures: true,
      minimap: { enabled: minimap, renderCharacters: false },
      wordWrap: wordWrap ? "on" : "off",
      smoothScrolling: true,
      cursorSmoothCaretAnimation: "on",
      cursorBlinking: "smooth",
      renderWhitespace: "boundary",
      scrollBeyondLastLine: false,
      automaticLayout: true,
      padding: { top: 8, bottom: 8 },
      lineNumbersMinChars: 3,
      glyphMargin: false,
      folding: true,
      bracketPairColorization: { enabled: true },
      guides: { bracketPairs: false, indentation: true },
      renderLineHighlight: "all",
      scrollbar: {
        verticalScrollbarSize: 12,
        horizontalScrollbarSize: 12,
        useShadows: false,
      },
      tabSize: 4,
      insertSpaces: true,
      stickyScroll: { enabled: false },
      contextmenu: true,
      fixedOverflowWidgets: true,
    }),
    [fontSize, minimap, wordWrap],
  );

  if (!activePath || !file) {
    return <Welcome />;
  }

  return (
    <div className="ed-root">
      <TabStrip />
      <Breadcrumb />
      <div className="ed-monaco">
        <Editor
          // Deliberately not keyed by path. Monaco keeps one model per `path`,
          // so switching files swaps models and keeps each file's undo history
          // and scroll position. Keying by path tore the whole editor down and
          // rebuilt it on every switch, which threw both away.
          path={activePath}
          language={language === "seal" ? "seal" : language === "json" ? "json" : "plaintext"}
          theme={editorTheme}
          value={file.content}
          options={options}
          onMount={onMount}
          onChange={handleChange}
          loading={<div className="ed-loading">Loading editor…</div>}
        />
      </div>
      <SealBuddy />
    </div>
  );
}
